import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import moment from 'moment';

import http from '@/utils/http';

const ActionButtons = ({ link, userId, onEdit, onDelete, onUpdate }) => {
  const [processing, setProcessing] = useState(false);

  const isActive = link.status === 'active';
  const isExpired = new Date(link.expires_at) < new Date();
  const isEditable = link.editable !== false;
  const smsSent = !!link.sent_via_sms;

  const btn = { fontSize: '0.72rem', padding: '2px 7px', marginRight: '5px' };

  const handleCopy = () => {
    setProcessing(true);
    http.post(`/magic_links/${link.id}/copy?user_id=${userId}`)
      .then((res) => {
        const url = res.data.url;
        try {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url);
          } else {
            const el = document.createElement('textarea');
            el.value = url;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
          }
        } catch (_) {}
        onUpdate({ ...link, copied: true, editable: false });
        window.flashMessages.addMessage({ type: 'success', text: 'Link copied to clipboard.' });
      })
      .catch(() => window.flashMessages.addMessage({ type: 'error', text: 'Failed to copy link.' }))
      .finally(() => setProcessing(false));
  };

  const handleSms = () => {
    setProcessing(true);
    http.post(`/magic_links/${link.id}/send_sms?user_id=${userId}`)
      .then((res) => {
        onUpdate(res.data);
        window.flashMessages.addMessage({ type: 'success', text: 'SMS sent successfully.' });
      })
      .catch((err) => window.flashMessages.addMessage({ type: 'error', text: err?.response?.data?.error || 'Failed to send SMS.' }))
      .finally(() => setProcessing(false));
  };

  const handleToggle = () => {
    const newStatus = isActive ? 'inactive' : 'active';
    setProcessing(true);
    http.put(`/magic_links/${link.id}?user_id=${userId}`, { magic_link: { status: newStatus } })
      .then((res) => {
        onUpdate(res.data);
        window.flashMessages.addMessage({ type: 'success', text: `Magic link ${newStatus === 'active' ? 'activated' : 'deactivated'}.` });
      })
      .catch(() => window.flashMessages.addMessage({ type: 'error', text: 'Failed to update status.' }))
      .finally(() => setProcessing(false));
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this magic link?')) return;
    setProcessing(true);
    http.delete(`/magic_links/${link.id}?user_id=${userId}`)
      .then(() => {
        onDelete(link.id);
        window.flashMessages.addMessage({ type: 'success', text: 'Magic link deleted.' });
      })
      .catch(() => window.flashMessages.addMessage({ type: 'error', text: 'Failed to delete.' }))
      .finally(() => setProcessing(false));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', flexWrap: 'wrap', width: '100%', gap: '5px' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={handleCopy}
        disabled={processing}
        style={btn}
      >
        Copy
      </button>

      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={handleSms}
        disabled={processing}
        style={btn}
      >
        {smsSent ? 'Resend' : 'SMS'}
      </button>

      {isEditable && !isExpired && (
        <button
          type="button"
          className="btn btn-sm btn-outline-info"
          onClick={() => onEdit(link)}
          disabled={processing}
          style={btn}
        >
          Edit
        </button>
      )}

      {!isExpired && (
        <button
          type="button"
          className={`btn btn-sm ${isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
          onClick={handleToggle}
          disabled={processing}
          style={btn}
        >
          {isActive ? 'Inactivate' : 'Activate'}
        </button>
      )}

      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        onClick={handleDelete}
        disabled={processing}
        title="Delete"
        style={{ padding: '2px 6px', lineHeight: 1, flexShrink: 0, marginLeft: '10px' }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
        </svg>
      </button>
    </div>
  );
};

const MagicLinksList = ({ magicLinks, userId, onEdit, onDelete, onUpdate }) => {
  const columns = [
    {
      Header: 'Drug',
      accessor: 'drug_name',
      width: 160,
    },
    {
      Header: 'Qty',
      accessor: 'qty',
      width: 55,
      Cell: ({ value }) => value != null ? value : '—',
    },
    {
      Header: 'Status',
      id: 'status',
      width: 80,
      Cell: ({ original }) => {
        const isExpired = new Date(original.expires_at) < new Date();
        const label = isExpired ? 'expired' : original.status;
        const color = isExpired ? 'danger' : (original.status === 'active' ? 'success' : 'secondary');
        return (
          <span
            className={`badge badge-${color}`}
            style={{ fontSize: '0.65rem', display: 'inline-block', minWidth: '52px', textAlign: 'center' }}
          >
            {label}
          </span>
        );
      },
    },
    {
      Header: 'Created',
      accessor: 'created_at',
      width: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: 'center', lineHeight: '1.3' }}>
          <div>{moment(value).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{moment(value).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      Header: 'Expires',
      accessor: 'expires_at',
      width: 150,
      Cell: ({ value }) => (
        <div style={{ textAlign: 'center', lineHeight: '1.3' }}>
          <div>{moment(value).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{moment(value).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      Header: 'Used',
      accessor: 'times_used',
      width: 60,
    },
    {
      Header: 'Last Used',
      accessor: 'last_used_at',
      width: 150,
      Cell: ({ value }) => value ? (
        <div style={{ textAlign: 'center', lineHeight: '1.3' }}>
          <div>{moment(value).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{moment(value).format('HH:mm')}</div>
        </div>
      ) : '—',
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      width: 120,
      getProps: () => ({ style: { overflow: 'hidden' } }),
      Cell: ({ value }) => value
        ? (
          <span
            title={value}
            style={{
              display: 'block',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {value}
          </span>
        )
        : '—',
    },
    {
      Header: 'Actions',
      id: 'actions',
      minWidth: 260,
      sortable: false,
      headerStyle: { textAlign: 'center', width: '100%' },
      Cell: ({ original }) => (
        <ActionButtons
          link={original}
          userId={userId}
          onEdit={onEdit}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      ),
    },
  ];

  return (
    <ReactTable
      minRows={1}
      columns={columns}
      data={magicLinks}
      pageSize={magicLinks.length || 1}
      showPagination={false}
      className="-striped -highlight"
      resizable={false}
      getTheadThProps={() => ({ style: { textAlign: 'center', fontWeight: '600', fontSize: '0.82rem', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' } })}
      getTdProps={() => ({ style: { overflow: 'visible', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem' } })}
      getNoDataProps={() => ({
        style: {
          top: 'unset',
          bottom: '0',
          transform: 'translateX(-50%)',
          height: '35px',
          display: 'flex',
          alignItems: 'center',
          background: 'transparent',
          pointerEvents: 'none',
        }
      })}
    />
  );
};

MagicLinksList.propTypes = {
  magicLinks: PropTypes.array.isRequired,
  userId: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default MagicLinksList;
